/**
 * React wrapper for Bryntum ColorPicker
 */
import React, { RefObject } from 'react';
import { AlignSpec, Base, Collection, CollectionConfig, ColorPicker, List, MaskConfig, Model, ModelConfig, Rectangle, Scroller, ScrollerConfig, Store, StoreConfig, TabConfig, TooltipConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumColorPickerProps =  {
     // Configs only
    activateOnMouseover?             : boolean
    adopt?                           : HTMLElement|string
    align?                           : AlignSpec|string
    allowGroupSelect?                : boolean
    anchor?                          : boolean
    ariaDescription?                 : string
    ariaLabel?                       : string
    bubbleEvents?                    : object
    callOnFunctions?                 : boolean
    centered?                        : boolean
    clearSelectionOnEmptySpaceClick? : boolean
    colorSelected?                   : (event: { record: Model, color: string }) => void
    config?                          : object
    constrainTo?                     : HTMLElement|Widget|Rectangle
    contentElementCls?               : string|object
    defaultBindProperty?             : string
    displayField?                    : string
    dock?                            : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?                       : boolean|{
        handleSelector?: string
    }
    floating?                        : boolean
    getItemCls?                      : ((record: Model) => string)|string
    getItemStyle?                    : ((record: Model) => string)|string
    groupHeaderTpl?                  : (record: Model, groupName: string) => string
    hideAnimation?                   : boolean|object
    htmlCls?                         : string|object
    ignoreParentReadOnly?            : boolean
    itemTpl?                         : (record: Model) => string
    listeners?                       : object
    localeClass?                     : typeof Base
    localizable?                     : boolean
    localizableProperties?           : string[]
    maskDefaults?                    : Partial<MaskConfig>
    masked?                          : boolean|string|Partial<MaskConfig>
    monitorResize?                   : boolean
    owner?                           : Widget
    positioned?                      : boolean
    preventTooltipOnTouch?           : boolean
    relayStoreEvents?                : boolean
    ripple?                          : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?                     : ShadowRoot
    scrollAction?                    : 'hide'|'realign'|null
    selectAllItem?                   : boolean|string
    showAnimation?                   : boolean|object
    showTooltipWhenDisabled?         : boolean
    store?                           : object|object[]|Store|Partial<StoreConfig>|Partial<StoreConfig>[]
    tab?                             : boolean|Partial<TabConfig>
    tag?                             : string
    textAlign?                       : 'left'|'center'|'right'|'start'|'end'
    title?                           : string
    ui?                              : string|object
    weight?                          : number

     // Configs and properties
    addNoColorItem?         : boolean
    alignSelf?              : string
    appendTo?               : HTMLElement|string
    cls?                    : string|object
    collapsibleGroups?      : boolean
    colors?                 : string[]
    columns?                : number
    content?                : string
    dataset?                : object
    disabled?               : boolean
    extraData?              : string|number|boolean|object
    flex?                   : number|string
    height?                 : number|string
    hidden?                 : boolean
    html?                   : string|((widget: Widget) => string)
    id?                     : string
    insertBefore?           : HTMLElement|string
    insertFirst?            : HTMLElement|string
    items?                  : object[]
    margin?                 : number|string
    maxHeight?              : string|number
    maximizeOnMobile?       : number|string
    maxWidth?               : string|number
    minHeight?              : string|number
    minWidth?               : string|number
    multiSelect?            : boolean
    readOnly?               : boolean
    scrollable?             : Scroller|boolean|Partial<ScrollerConfig>
    selected?               : Model|Model[]|string|string[]|number|number[]|Collection|object[]|Partial<CollectionConfig>|Partial<ModelConfig>[]|Partial<CollectionConfig>[]
    toggleAllIfCtrlPressed? : boolean
    tooltip?                : string|Partial<TooltipConfig>
    width?                  : number|string
    x?                      : number
    y?                      : number

     // Properties only
    anchorSize? : number[]
    type?       : string

     // Events
    onBeforeDestroy?   : ((event: { source: Base }) => void)|string
    onBeforeHide?      : ((event: { source: Widget }) => boolean|void)|string
    onBeforeItem?      : ((event: { source: List, record: Model, args: number, event: Event }) => boolean|void)|string
    onBeforeShow?      : ((event: { source: Widget|any }) => Promise<boolean|void>)|string
    onCatchAll?        : ((event: {[key: string]: any, type: string}) => void)|string
    onDestroy?         : ((event: { source: Base }) => void)|string
    onFocusIn?         : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?        : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onHide?            : ((event: { source: Widget }) => void)|string
    onItem?            : ((event: { source: List, record: Model, index: number, event: Event }) => void)|string
    onPaint?           : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onReadOnly?        : ((event: { readOnly: boolean }) => void)|string
    onRecompose?       : (() => void)|string
    onResize?          : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onSelectionChange? : ((event: { source: List, selected: Model[] }) => void)|string
    onShow?            : ((event: { source: Widget }) => void)|string
    onToggleGroup?     : ((event: { groupRecord: Model, collapse: boolean }) => void)|string

}

export class BryntumColorPicker extends React.Component<BryntumColorPickerProps> {

    static instanceClass = ColorPicker;

    static instanceName = 'ColorPicker';

    processWidgetContent = processWidgetContent;

    static configNames = [
        'activateOnMouseover',
        'adopt',
        'align',
        'allowGroupSelect',
        'anchor',
        'ariaDescription',
        'ariaLabel',
        'bubbleEvents',
        'callOnFunctions',
        'centered',
        'clearSelectionOnEmptySpaceClick',
        'colorSelected',
        'config',
        'constrainTo',
        'contentElementCls',
        'defaultBindProperty',
        'displayField',
        'dock',
        'draggable',
        'floating',
        'getItemCls',
        'getItemStyle',
        'groupHeaderTpl',
        'hideAnimation',
        'htmlCls',
        'ignoreParentReadOnly',
        'itemTpl',
        'listeners',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'monitorResize',
        'owner',
        'positioned',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'ripple',
        'rootElement',
        'scrollAction',
        'selectAllItem',
        'showAnimation',
        'showTooltipWhenDisabled',
        'store',
        'tab',
        'tag',
        'textAlign',
        'title',
        'ui',
        'weight'
    ];

    static propertyConfigNames = [
        'addNoColorItem',
        'alignSelf',
        'appendTo',
        'cls',
        'collapsibleGroups',
        'colors',
        'columns',
        'content',
        'dataset',
        'disabled',
        'extraData',
        'flex',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'minHeight',
        'minWidth',
        'multiSelect',
        'onBeforeDestroy',
        'onBeforeHide',
        'onBeforeItem',
        'onBeforeShow',
        'onCatchAll',
        'onDestroy',
        'onFocusIn',
        'onFocusOut',
        'onHide',
        'onItem',
        'onPaint',
        'onReadOnly',
        'onRecompose',
        'onResize',
        'onSelectionChange',
        'onShow',
        'onToggleGroup',
        'readOnly',
        'scrollable',
        'selected',
        'toggleAllIfCtrlPressed',
        'tooltip',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'type'
    ];

    // Component instance
    instance!: ColorPicker;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumColorPickerProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-color-picker-container`;
        return (
            <div className={className} ref={(element) => (this.element = element!)}></div>
        );

    }
}
